<script setup lang="ts">
import { NOOP } from "@vue/shared";
import { computed, inject, nextTick, onMounted, provide, ref, toRef, watch } from "vue";
import { usePromiseLazy } from "vue-composable";
import { openModal, openPage } from "../../../../composables/dialog/drawer";
import { showDialog } from "../../../../utils/dialog";
import { usePrescriptionStore } from "../../../../store/transitient/prescription";
import { SearchPredefinedDosagesQueryResult } from "../../../../store/transitient/prescription/api/types";
import {
  BaseEnumTrait,
  NewPrescriptionResponse,
} from "../../../../store/transitient/prescription/types";
import axios from "../../../../utils/axios";
import { generateUUIDv7, randomID } from "../../../../utils/helpers";
import MBanner from "../../../medicus/MBanner";
import MButton from "../../../medicus/MButton";
import MCheckboxGroup from "../../../medicus/MCheckboxGroup";
import MCreateForm from "../../../medicus/MCreateForm";
import MInputGroup from "../../../medicus/MInputGroup";
import MLayoutStack from "../../../medicus/MLayoutStack";
import MRadioGroup from "../../../medicus/MRadioGroup";
import MCheckbox from "../../../medicus/inputs/MCheckbox";
import MDate from "../../../medicus/inputs/MDate";
import MInput from "../../../medicus/inputs/MInput";
import MSimpleSelect from "../../../medicus/inputs/MSimpleSelect";
import MSearchSelect from "../../../medicus/inputs/MSearchSelect";
import MSelectList from "../../../medicus/inputs/MSelectList";
import MSection from "../../../medicus/MSection";
import DrugOrDeviceSelector from "../DrugOrDeviceSelector";
import DosageInstructionInput from "./DosageInstructionInput";
import {
  EntryType,
  SortOrder,
} from "../../../medicus/consultation/MConsultationTopicHeading/types";
import { formatDate } from "../../../../utils/date";
import { useDrawerStore } from "../../../../store/drawers";
import CurrentAllergies from "../CurrentAllergies";
import { determineOptimiseRxBannerPosition, OptimiseRxBanners } from "../OptimiseRx";
import {
  convertProspectiveToCurrent,
  deleteOptimiseRxPatient,
} from "../../../../store/transitient/prescription/api";

const reloadData = inject("reloadData", NOOP as () => Promise<void>);
const close = inject("close", NOOP);
const success = inject<(_shouldClose: boolean, data?: {}) => void>("success", NOOP);
const additionalCreatePayloadData = inject<{}>("additionalCreatePayloadData");
const drawerOptions = inject<Record<string, any>>("drawerOptions");

const prescriptionAdditionalCreatePayloadData = ref(additionalCreatePayloadData || {});

provide("additionalCreatePayloadData", prescriptionAdditionalCreatePayloadData);

const props = defineProps<{
  formUrl: string;
  hideAddAnother?: boolean;
  hideIssueNow?: boolean;
  showIssueLaterButton?: boolean;
  issueLaterLabel?: string;
  issueLaterColor?: string;
  additionalIgnoreFields?: string[];
  createdInPatientRequestTask?: boolean;
  afterSuccess?: Function;
}>();

const afterSuccess = props.afterSuccess ?? null;

const store = usePrescriptionStore();

const prescription = toRef(store, "prescription");

const formKey = ref(randomID());

const isNewProblem = ref(false);
const showReviewDateInput = ref(false);

function toggleNewProblem() {
  isNewProblem.value = !isNewProblem.value;
}

provide("formData", store.form);

provide("ignoreFields", [
  "prescription.allowedPrescriptionTypes",
  "prescription.dosageInstruction.maximumPeriodInDays",
  ...(props.additionalIgnoreFields ?? []),
]);

let addAnother = false;

onMounted(() => {
  if (
    additionalCreatePayloadData &&
    "sortOrder" in additionalCreatePayloadData &&
    additionalCreatePayloadData?.sortOrder
  ) {
    store.sortOrder = additionalCreatePayloadData.sortOrder as SortOrder;
  }
});

function beforeIssueLater() {
  addAnother = false;

  deleteOptimiseRxPatient(store.state.optimiseRxUserId);
}

function beforeSaveAndAddAnother() {
  // TODO
  addAnother = true;

  convertProspectiveToCurrent(store.state.optimiseRxUserId, store.state.prescription.productCode);
}

function beforeSubmit() {
  // TODO
}

async function goToTask(taskId) {
  close();

  return await openPage(`/tasks/eps-subsequent-cancellation/overview/${taskId}`);
}

function parseDialogResponse(response, cb) {
  if (response?.data?.showTaskDialog) {
    if (response?.data?.taskId) {
      return showDialog({
        title: "EPS Cancellation Failure",
        text: response.data.message,
        cancelLabel: "Dismiss",
        okLabel: "Go to task",
        okColor: "submit",
      })
        .onOk(async () => {
          await goToTask(response.data.taskId);
          cb?.();
        })
        .onCancel(() => {
          cb?.();
        });
    }

    return showDialog({
      title: "EPS Cancellation Failure",
      text: response.data.message,
      okLabel: "Dismiss",
      okColor: "secondary",
      noCancel: true,
    }).onOk(() => {
      cb?.();
    });
  }
}

function onError(error) {
  if (error.response?.data?.showTaskDialog) {
    const cb = () => afterSuccess?.(error.response?.data || undefined);

    close();
    parseDialogResponse(error.response, () => onSubmitResponseCallback(error.response, cb));
  }
}

function onSuccess(response: NewPrescriptionResponse) {
  success?.(false, response?.data || undefined);
  const cb = () => afterSuccess?.(response?.data || undefined);

  if (response?.data?.showTaskDialog) {
    close();
    parseDialogResponse(response, () => onSubmitResponseCallback(response, cb));

    return;
  }

  onSubmitResponseCallback(response, cb);
}

async function onSubmitResponseCallback(response: NewPrescriptionResponse, cb) {
  formKey.value = randomID();
  if (addAnother) {
    try {
      if (response?.data?.newProblemId) {
        if (
          store.state.linkableProblems.findIndex(
            (x) => x.conceptId === store.form.newLinkedProblem.conceptId,
          ) === -1
        ) {
          store.state.linkableProblems.push({
            value: response.data.newProblemId,
            hasEnded: false,
            label: store.form.newLinkedProblem.description,
            conceptId: store.form.newLinkedProblem.conceptId,

            endDate: null,
            startDate: null,
            isMarkedIncorrect: false,
          });
        }
      }
      reloadData();
      store.resetPrescriptionStructure();
      store.prescription.prescriberEndorsements = null;
      store.prescription.dispenserInstructions = null;
      store.drugOrDevice = null;
      store.dosage = null;

      selectedDosage.value = null;
      store.state.confidentialFromThirdParties = false;
      store.state.hiddenFromPatientFacingServices = false;
      store.state.confirmationAcknowledgedAndProceed = false;
      store.state.confirmationAcknowledgedAndProceedSafetyChecks = false;
      store.state.optimiseRxRejectionReason = null;
      clearProblems();
      isNewProblem.value = false;
      if (response?.data?.sortOrderHash) {
        const newPrescriptionId = generateUUIDv7();
        store.newPrescriptionId = newPrescriptionId;
        let newSortOrder: SortOrder = response?.data?.sortOrder ?? [];
        newSortOrder.push({
          id: newPrescriptionId,
          entryType: EntryType.PRESCRIPTION,
        });
        store.sortOrder = newSortOrder;

        const newAdditionalCreatePayloadData = {
          uuid: newPrescriptionId,
          sortOrder: store.sortOrder,
          sortOrderHash: response.data.sortOrderHash,
        };
        store.sortOrderHash = response.data.sortOrderHash;

        prescriptionAdditionalCreatePayloadData.value = newAdditionalCreatePayloadData;
      }
    } finally {
      addAnother = false;
    }
  } else {
    useDrawerStore().clear();
    reloadData();

    await deleteOptimiseRxPatient(store.state.optimiseRxUserId);

    if (store.form.workflowIdentifier && true !== store.state.skipIssuing) {
      nextTick(() => {
        openModal(
          `/clinical/prescription/modal/issue-prescription/${store.form.workflowIdentifier}`,
          {
            autoFocus: true,
            props: {
              createdInPatientRequestTask: props.createdInPatientRequestTask,
            },
            onSuccess: drawerOptions?.onSuccess,
          },
        );
      });
    }
  }

  cb?.();
}

const computedOptimiseRxIssueQuantityResults = computed(() => {
  let results = [];

  store.optimiseRxProspectiveDrugResultSet?.forEach((result) => {
    result.metaDataItems.forEach((metaDataItem) => {
      let bannerPosition = determineOptimiseRxBannerPosition(result, metaDataItem);

      if ("issue-quantity" === bannerPosition) {
        if (results.filter((res) => res.ruleId === result.ruleId).length === 0) {
          results.push(result);
        }
      }
    });
  });

  return results;
});

const computedRepeatReauthoriseActionType = computed(() => {
  const sanitise = (input) => {
    return input?.replace(/[^a-zA-Z0-9.-]/g, "");
  };

  if ("indeterminate" !== store.prescriptionFormType) {
    return false;
  } else if (!store.originalDrugOrDevice?.vmp?.conceptId) {
    // If the original prescription's drug or device is not in dm+d, it will not return
    // VMP data, and therefore we must assume that it is a modify action
    return "modify";
  } else if (!store.drugOrDevice?.vmp?.conceptId) {
    return false;
  } else if (
    (store.originalDrugOrDevice.vmp?.conceptId !== store.drugOrDevice.vmp?.conceptId &&
      store.originalDrugOrDevice.vmp?.description !== store.drugOrDevice.vmp?.description) ||
    sanitise(store.originalDosageInstructionText) !==
      sanitise(store.prescription.dosageInstruction.dosageText)
  ) {
    return "modify";
  } else if (
    (store.originalDrugOrDevice.vmp?.conceptId === store.drugOrDevice.vmp?.conceptId ||
      store.originalDrugOrDevice.vmp?.description === store.drugOrDevice.vmp?.description) &&
    sanitise(store.originalDosageInstructionText) ===
      sanitise(store.prescription.dosageInstruction.dosageText)
  ) {
    return "re-authorise";
  }

  return false;
});

const computedRepeatReauthoriseBannerMessage = computed(() => {
  if ("re-authorise" === computedRepeatReauthoriseActionType.value) {
    return 'There have been no changes to the medication or dosage which means that Medicus will create a new prescription and mark the old prescription "re-authorised".';
  } else if ("modify" === computedRepeatReauthoriseActionType.value) {
    return 'Because you have made changes to the medication or dosage, Medicus will create a new prescription and mark the old prescription "ended" with the reason "Change to medication treatment regimen".';
  }

  return null;
});

const computedExpectedDaysSupply = computed(() => {
  if (false === store.prescription?.dosageInstruction?.useManualDosageText) {
    if (
      store.prescription?.dosageInstruction?.dose?.snomedCtCode?.conceptId !==
      store.prescription?.issueQuantity?.snomedCtCode?.conceptId
    ) {
      return 1;
    }

    return Math.ceil(
      (store.prescription.issueQuantity.value *
        ((store.prescription.dosageInstruction.maximumPeriodInDays || 1) /
          (store.prescription.dosageInstruction.frequency || 1))) /
        (store.prescription.dosageInstruction.dose?.value || 1),
    );
  }

  return store.prescription?.expectedDaysSupply;
});

const confirmationRequiredMessage = computed(() => {
  const expectedDaysSupply = computedExpectedDaysSupply.value;

  // Schedule 2, 3, 4
  if (["2", "3", "4"].indexOf(store.prescription.category) !== -1) {
    if (expectedDaysSupply > 30) {
      const baseMessage =
        "Schedule 2, 3 or 4 drugs should be limited to a maximum supply duration of 30 days.";
      if (store.prescription?.dosageInstruction?.useManualDosageText) {
        return `${baseMessage} You have entered ${expectedDaysSupply} days.`;
      }

      return `${baseMessage}\n\nThe issue quantity entered would equate to a ${expectedDaysSupply} day supply based on the dosage instructions.`;
    }

    return null;
  }

  // HRT
  if (store.metaInformation?.isHrt) {
    if (expectedDaysSupply > 365) {
      const baseMessage =
        "HRT medications should be limited to a maximum supply duration of 365 days.";
      if (store.prescription?.dosageInstruction?.useManualDosageText) {
        return `${baseMessage} You have entered ${expectedDaysSupply} days.`;
      }

      return `${baseMessage}\n\nThe issue quantity entered would equate to a ${expectedDaysSupply} day supply based on the dosage instructions.`;
    }

    return null;
  }

  // Oral contraceptive
  if (store.metaInformation?.isOralContraceptive) {
    if (expectedDaysSupply > 365) {
      const baseMessage =
        "Oral contraceptives should be limited to a maximum supply duration of 365 days.";
      if (store.prescription?.dosageInstruction?.useManualDosageText) {
        return `${baseMessage} You have entered ${expectedDaysSupply} days.`;
      }

      return `${baseMessage}\n\nThe issue quantity entered would equate to a ${expectedDaysSupply} day supply based on the dosage instructions.`;
    }

    return null;
  }

  // Everything else
  if (expectedDaysSupply > 90) {
    const baseMessage =
      "It is not recommended to supply medication for more than 90 days at a time.";
    if (store.prescription?.dosageInstruction?.useManualDosageText) {
      return `${baseMessage} You have entered ${expectedDaysSupply} days.`;
    }

    return `${baseMessage}\n\nThe issue quantity entered would equate to a ${expectedDaysSupply} day supply based on the dosage instructions.`;
  }

  return null;
});

const prescribingStaffList = computed(() => {
  return store.state.isInNurseFormulary
    ? store.state.prescribingStaff
    : store.state.prescribingStaffExcludingCommunityNurses;
});

const disableSubmit = computed(() => {
  return (
    !store.drugOrDevice ||
    (confirmationRequiredMessage.value && !store.state.confirmationAcknowledgedAndProceed) ||
    (store.computedCountOfDangerTypeSafetyChecks > 0 &&
      !store.state.confirmationAcknowledgedAndProceedSafetyChecks) ||
    repeatDispensingFailsOnNumberOfIssues.value ||
    repeatDispensingFailsOnDuration.value ||
    repeatFailsOnNumberOfIssues.value
  );
});

const prescriptionTypes = computed(() => {
  return store.metaInformation?.allowedPrescriptionTypes?.length
    ? store.metaInformation?.allowedPrescriptionTypes
    : store.state.defaultPrescriptionTypes;
});

const isExpectedDaysSupplyRequired = computed(
  () =>
    prescription.value.prescriptionType === "repeat" ||
    prescription.value.prescriptionType === "repeat-dispensing",
);

const showNumberOfIssues = computed(
  () =>
    prescription.value.authorisationMethod === "fixed-number-of-issues" &&
    (prescription.value.prescriptionType === "repeat" ||
      prescription.value.prescriptionType === "repeat-dispensing"),
);

const showReviewDate = computed(
  () =>
    prescription.value.prescriptionType === "variable-repeat" ||
    prescription.value.prescriptionType === "repeat" ||
    prescription.value.prescriptionType === "repeat-dispensing",
);

const computedLatestReviewInformationBannerType = computed(() => {
  const latestReviewInformation = store.latestReviewInformation;
  if (latestReviewInformation === null) {
    return null;
  }

  if (latestReviewInformation.type === "future-action") {
    return latestReviewInformation.reviewDateHasExpired ? "warning" : "info";
  }

  if (latestReviewInformation.type === "care-record-note") {
    return latestReviewInformation.reviewDateHasExpired ? "warning" : "info";
  }

  return "warning";
});

const computedLatestReviewInformationBannerMessage = computed(() => {
  const latestReviewInformation = store.latestReviewInformation;
  if (latestReviewInformation === null) {
    return null;
  }

  if (latestReviewInformation.type === "future-action") {
    return `Next review due ${formatDate(latestReviewInformation.reviewDate)}`;
  }

  if (latestReviewInformation.type === "care-record-note") {
    return `Last review ${formatDate(latestReviewInformation.reviewDate)}`;
  }

  return "Medicus was unable to determine whether the patient has had a review of their medication";
});

// NOTE BUSINESS RULE FROM NHS!
const MaximumNumberOfIssuesForRepeatDispensing = 99;
const MinimumNumberOfIssuesForRegularRepeatAndRepeatDispensingTypes = 1;

const repeatDispensingFailsOnMaximumNumberOfIssues = computed(() => {
  if (prescription.value.prescriptionType !== "repeat-dispensing") return false;
  return prescription.value.numberOfIssues! > MaximumNumberOfIssuesForRepeatDispensing;
});

const repeatDispensingFailsOnNumberOfIssues = computed(() => {
  if (prescription.value.prescriptionType !== "repeat-dispensing") return false;
  return (
    repeatDispensingFailsOnMaximumNumberOfIssues.value ||
    prescription.value.numberOfIssues! <
      MinimumNumberOfIssuesForRegularRepeatAndRepeatDispensingTypes
  );
});

const repeatFailsOnNumberOfIssues = computed(() => {
  if (prescription.value.prescriptionType !== "repeat") return false;
  if (prescription.value.authorisationMethod === "review-date") return false;

  return (
    prescription.value.numberOfIssues! <
    MinimumNumberOfIssuesForRegularRepeatAndRepeatDispensingTypes
  );
});

const minimumIssuesTextForRepeatTypes = computed(() => {
  if (prescription.value.prescriptionType == "repeat")
    return "Repeat prescriptions must have a minimum of 1 authorised issue.";
  return "Repeat dispensing prescriptions must have a minimum of 1 authorised issue.";
});

// NOTE BUSINESS RULE FROM NHS!
const MaximumAllowedExpectedDaysForRepeatDispensing = 365;
const repeatDispensingFailsOnDuration = computed(() => {
  if (prescription.value.prescriptionType !== "repeat-dispensing") return false;
  return prescription.value.expectedDaysSupply && prescription.value.numberOfIssues
    ? +prescription.value.expectedDaysSupply * prescription.value.numberOfIssues >
        MaximumAllowedExpectedDaysForRepeatDispensing
    : false;
});

const expectedDaysSupplyRules = [
  (v: string) => {
    if (!v) return true;
    const d = +v;

    if (d < 1) {
      return "Expected days supply should be 1 or more";
    }

    return true;
  },
];

const selectedDosage = ref<SearchPredefinedDosagesQueryResult | null>(null);

const linkableProblems = computed(() => {
  if (!store.state.linkableProblems) return undefined;
  const sorted = [...store.state.linkableProblems].sort((a, b) => a.label.localeCompare(b.label));

  const archived = sorted.filter((x) => x.hasEnded);
  const active = sorted.filter((x) => !x.hasEnded);

  const result = [];
  if (active.length) result.push({ title: "Active Problems", items: active });
  if (archived.length) result.push({ title: "Inactive Problems", items: archived });
  return result;
});

const {
  result: prescribingStaff,
  loading: loadingPrescribingStaff,
  exec: refreshPrescribingStaff,
} = usePromiseLazy(
  (date: string, productCode: string) =>
    axios
      .get<{ activeStaff: BaseEnumTrait[] }>("/staff/data/active-prescribing-staff", {
        cancelRequestId: "active-prescribing-staff",
        params: { date, productCode },
      })
      .then((x) => x.data),
  true,
);

const areFieldsDisabled = computed(() => {
  return !store.drugOrDevice;
});

watch(
  () => store.prescription?.expectedDaysSupply,
  () => {
    store.state.confirmationAcknowledgedAndProceed = false;
  },
);

watch(
  () => prescription.value?.issueQuantity,
  () => {
    if (prescription.value.dosageInstruction?.useManualDosageText) {
      return;
    }

    store.state.confirmationAcknowledgedAndProceed = false;
  },
);

watch(
  () => store.drugOrDevice,
  async (data) => {
    if (!data) {
      store.resetPrescriptionStructure();
    }
  },
);

watch(
  [() => prescription.value?.authorisationMethod, () => prescription.value?.prescriptionType],
  ([authorisationMethod, prescriptionType]) => {
    if ("review-date" === authorisationMethod && "repeat" === prescriptionType) {
      store.fetchLatestReviewInformation();
      return;
    }
  },
);

watch(
  () => prescription.value?.dosageInstruction.useManualDosageText,
  async (useManualDosageText) => {
    if (useManualDosageText === true) {
      prescription.value.issueQuantity.value = null;
      store.state.confirmationAcknowledgedAndProceed = false;
    }
  },
);

watch(
  () => prescription.value?.startDate,
  async (date) => {
    const { activeStaff } = (await refreshPrescribingStaff(date, prescription.value.productCode))!;
    if (activeStaff.findIndex((x) => x.value === prescription.value.authorisedBy) === -1) {
      prescription.value.authorisedBy = null;
    }
  },
);

watch(
  () => prescription.value?.prescriptionType,
  async (type) => {
    switch (type) {
      case "repeat":
      case "repeat-dispensing":
        prescription.value.numberOfIssues = null;
        break;
      default:
        prescription.value.numberOfIssues = 1;
    }

    store.state.confirmationAcknowledgedAndProceed = false;
  },
);

function clearProblems() {
  store.form.newLinkedProblem = null;
  store.form.linkedProblemIds = [];
}
watch(isNewProblem, clearProblems);
watch(
  computedRepeatReauthoriseActionType,
  (action) => {
    if (action) {
      store.form.action = action;
    }
  },
  { immediate: true },
);
</script>
<template>
  <MCreateForm
    :key="formKey"
    class="create-prescription-form"
    :url="formUrl"
    :disable-submit="disableSubmit"
    hide-cancel
    no-close
    @success="onSuccess"
    @error="onError"
  >
    <template #footer>
      <m-layout-stack v-if="store.drugOrDevice" horizontal end full>
        <MButton
          v-if="!hideAddAnother"
          label="Create another"
          color="secondary"
          :disable="disableSubmit"
          :permission="{
            url: '/clinical/prescription/create/create-prescription/authorise',
            method: 'POST',
          }"
          type="submit"
          data-testid="add-another-button"
          @click="beforeSaveAndAddAnother"
        />
        <MButton
          v-if="showIssueLaterButton"
          :label="issueLaterLabel || 'Issue later'"
          :color="~[null, undefined].indexOf(issueLaterColor) ? 'secondary' : issueLaterColor"
          :disable="disableSubmit"
          :permission="{
            url: '/clinical/prescription/create/create-prescription/authorise',
            method: 'POST',
          }"
          type="submit"
          data-testid="issue-later-button"
          @click="beforeIssueLater"
        />
        <MButton
          v-if="!hideIssueNow"
          label="Authorise & continue"
          :disable="disableSubmit"
          :permission="{
            url: '/clinical/prescription/create/create-prescription/authorise',
            method: 'POST',
          }"
          type="submit"
          data-testid="issue-now-button"
          @click="beforeSubmit"
        />
      </m-layout-stack>
      <div v-else />
    </template>
    <CurrentAllergies data-testid="current-allergies" />
    <DrugOrDeviceSelector />
    <m-banner v-if="store.drugOrDevice?.isSpecialOrder" type="warning">
      This is a special order product. Please refer to the
      <m-link
        style="display: inline"
        target="_blank"
        :href="`https://bnfc.nice.org.uk/medicines-guidance/special-order-manufacturers/`"
        >BNF guidelines</m-link
      >
      for special order products.
    </m-banner>

    <DosageInstructionInput
      v-if="store.drugOrDevice"
      name="prescription.dosageInstruction.dosageText"
      label="Free text dosage instructions"
      :manual-dosage="prescription.dosageInstruction.useManualDosageText"
      required
      autogrow
      dense
      maxlength="700"
      counter
      :disable="areFieldsDisabled"
    />

    <m-section v-if="store.drugOrDevice" title="Supply">
      <m-radio-group
        name="prescription.prescriptionType"
        label="Prescription type"
        required
        :items="prescriptionTypes"
        :disabled="areFieldsDisabled"
      />

      <m-input
        type="number"
        name="prescription.expectedDaysSupply"
        label="Expected days supply"
        suffix="days"
        :rules="expectedDaysSupplyRules"
        min="1"
        :required="isExpectedDaysSupplyRequired"
        debounce="500"
        :loading="store.issueQuantity.loading"
        data-testid="expectedDaysSupply"
        :disabled="areFieldsDisabled"
      />
      <m-input
        v-if="store.state.preventManualIssueQuantityEntry"
        name="prescription.issueQuantity.value"
        required
        data-testid="issue-quantity-fixed-input"
        type="number"
        label="Issue quantity"
        :suffix="prescription.issueQuantity.snomedCtCode?.description"
        :disabled="areFieldsDisabled"
      />
      <m-input-group
        v-else
        horizontal
        label="Issue quantity"
        left
        required
        data-testid="issue-quantity-input-group"
      >
        <m-input
          name="prescription.issueQuantity.value"
          required
          small
          type="number"
          label="Value"
          :disabled="areFieldsDisabled"
        />
        <m-simple-select
          name="prescription.issueQuantity.snomedCtCode"
          :options="store.unitOfMeasures"
          clearable
          required
          label="Unit"
          :disabled="areFieldsDisabled"
        />
      </m-input-group>

      <OptimiseRxBanners
        v-if="store.drugOrDevice"
        :results="computedOptimiseRxIssueQuantityResults"
        :expanded="true"
      />

      <m-banner
        v-if="repeatDispensingFailsOnDuration"
        type="warning"
        data-testid="repeat-dispensing-duration-banner"
      >
        <p>
          The total duration for repeat dispensing prescriptions can not exceed 12 months. Please
          reduce the number of authorised issues or change the prescription type to repeat.
        </p>
      </m-banner>

      <m-input
        label="Additional instructions for dispenser"
        name="prescription.dispenserInstructions"
        :disabled="areFieldsDisabled"
      />
    </m-section>

    <m-section v-if="store.drugOrDevice" title="Authorisation">
      <m-date
        label="Start date"
        required
        name="prescription.startDate"
        :disabled="areFieldsDisabled"
      />

      <m-radio-group
        v-if="prescription.prescriptionType === 'repeat'"
        label="Authorisation method"
        :items="store.state.authorisationMethods"
        :value="prescription.authorisationMethod"
        :model-value="prescription.authorisationMethod"
        name="prescription.authorisationMethod"
        required
      />

      <m-input
        v-if="showNumberOfIssues"
        name="prescription.numberOfIssues"
        label="Number of issues"
        required
        type="number"
        min="1"
        :min-text="minimumIssuesTextForRepeatTypes"
        :required-text="minimumIssuesTextForRepeatTypes"
        :disabled="areFieldsDisabled"
      />
      <m-banner v-if="repeatDispensingFailsOnMaximumNumberOfIssues" type="warning">
        <p>
          A repeat dispensing prescription can have a maximum of
          {{ MaximumNumberOfIssuesForRepeatDispensing }} authorised issues. Please reduce the number
          of authorised issues or change the prescription type to repeat.
        </p>
      </m-banner>

      <m-date
        v-if="showReviewDate && 'fixed-number-of-issues' === prescription.authorisationMethod"
        name="prescription.reviewDate"
        label="Review date"
        :min-date="store.state.minimumReviewDate"
        :max-date="store.state.maximumReviewDate"
        :disabled="areFieldsDisabled"
      />

      <m-labeled-text
        v-if="
          showReviewDate &&
          false === showReviewDateInput &&
          'review-date' === prescription.authorisationMethod &&
          !prescription.reviewDate
        "
        label="Review date (this prescription only)"
      >
        Not required &nbsp; <m-link @click="showReviewDateInput = true">Add</m-link>
      </m-labeled-text>

      <m-labeled-text
        v-else-if="
          showReviewDate &&
          false === showReviewDateInput &&
          'review-date' === prescription.authorisationMethod &&
          prescription.reviewDate
        "
        label="Review date (this prescription only)"
      >
        {{ $formatDate(prescription.reviewDate) }} &nbsp;
        <m-link @click="showReviewDateInput = true">Override</m-link>
      </m-labeled-text>

      <m-date
        v-if="
          showReviewDate &&
          showReviewDateInput &&
          'review-date' === prescription.authorisationMethod
        "
        name="prescription.reviewDate"
        label="Review date (this prescription only)"
        :min-date="store.state.minimumReviewDate"
        :max-date="store.state.maximumReviewDate"
        :disabled="areFieldsDisabled"
      />

      <m-banner
        v-if="
          store.latestReviewInformation?.isLoading === true &&
          'review-date' === prescription.authorisationMethod
        "
        type="system"
      >
        Loading review date information...
      </m-banner>
      <m-banner
        v-else-if="
          store.latestReviewInformation?.isLoading === false &&
          computedLatestReviewInformationBannerType &&
          'review-date' === prescription.authorisationMethod
        "
        :type="computedLatestReviewInformationBannerType"
      >
        {{ computedLatestReviewInformationBannerMessage }}
      </m-banner>

      <m-simple-select
        name="prescription.authorisedBy"
        label="Authorised by practitioner"
        required
        :options="prescribingStaff?.activeStaff ?? prescribingStaffList"
        :loading="loadingPrescribingStaff"
        :disabled="areFieldsDisabled"
      />

      <m-banner
        v-if="store.state.authoriseOrganisationIsLocal == false"
        :disabled="areFieldsDisabled ? true : undefined"
        type="info"
      >
        <span>A prescriber from the local organisation must authorise the prescription.</span>
      </m-banner>

      <m-banner
        v-if="
          false === store.state.isInNurseFormulary &&
          false === store.state.actorCanPrescribeNonNurseFormularyProducts &&
          !prescription.authorisedBy
        "
        data-testid="nurse-formulary-restriction-banner"
      >
        You have been setup as a Community Nurse prescriber. The medication being prescribed is
        outside of the national nurse formulary and therefore cannot be authorised by you. Please
        contact your Medicus administrator if you think this is incorrect.
      </m-banner>

      <m-checkbox-group
        v-if="store.state.allowablePrescriberEndorsements?.length"
        label="Prescriber endorsement"
        name="prescription.prescriberEndorsements"
        :items="store.state.allowablePrescriberEndorsements"
        :disable="areFieldsDisabled"
        :tabindex-override="0"
      />
    </m-section>

    <template v-if="!store.form.contextId && store.drugOrDevice">
      <m-section title="Indication">
        <m-search-select
          v-if="isNewProblem"
          v-model="store.form.newLinkedProblem"
          label="New problem code"
          search-url="clinical/gb/snomed/search/description/constrained?constrainingParentConcepts=404684003&excludeConstrainingConcepts=307824009"
          placeholder="Search problem codes"
          data-testid="create-new-problem"
          actions-left
          :disabled="areFieldsDisabled"
          :actions="[
            {
              label: 'Link to existing problem',
              click: toggleNewProblem,
              disabled: areFieldsDisabled,
              'data-testid': 'use-existing-problem-button',
            },
          ]"
        />
        <m-select-list
          v-else
          v-model="store.form.linkedProblemIds"
          outlined
          :options="linkableProblems"
          label="Linked problem"
          data-testid="link-to-problem"
          :render-text="(_, item) => item.label"
          :disabled="areFieldsDisabled"
          actions-left
          :actions="[
            {
              label: 'Add new',
              click: toggleNewProblem,
              disabled: areFieldsDisabled,
              'data-testid': 'create-new-problem-button',
            },
          ]"
        />
      </m-section>
    </template>
    <m-section v-if="store.drugOrDevice && !store.form.contextType" title="Confidentiality">
      <m-checkbox-group>
        <m-checkbox
          data-testid="confirm-confidential-from-third-parties"
          label="Confidential from third parties"
          name="confidentialFromThirdParties"
          :disable="areFieldsDisabled"
        />
        <m-checkbox
          data-testid="confirm-hidden-from-patient-facing-services"
          label="Redacted from patient online access"
          name="hiddenFromPatientFacingServices"
          :disable="areFieldsDisabled"
        />
      </m-checkbox-group>
    </m-section>

    <m-banner
      v-if="
        store.metaInformation?.canPersonallyAdminister &&
        !store.metaInformation?.isFp34dHighVolumeVaccine &&
        ~['acute', 'repeat', 'variable-repeat'].indexOf(prescription.prescriptionType)
      "
      type="info"
      data-testid="personally-administered-message"
    >
      <p class="q-mb-md whitespace-pre-wrap">
        This medication is eligible for FP34D/PD personal administration claims.
      </p>

      <m-checkbox
        v-model="store.prescription.fp34dPersonallyAdministeredClaim"
        label="This prescription will be personally administered and claimed for"
      />
    </m-banner>

    <m-banner
      v-if="store.drugOrDevice && store.computedCountOfDangerTypeSafetyChecks"
      type="warning"
      data-testid="safety-alert-confirmation"
    >
      <p class="q-mb-md whitespace-pre-wrap">
        There are sensitivity alerts on the right hand side of this form indicating that this is a
        potentially risky prescription. Please review and acknowledge before proceeding.
      </p>

      <m-checkbox
        v-model="store.state.confirmationAcknowledgedAndProceedSafetyChecks"
        label="Acknowledge and proceed"
      />
    </m-banner>

    <m-banner v-if="store.drugOrDevice && confirmationRequiredMessage" type="warning">
      <p class="q-mb-md whitespace-pre-wrap">
        {{ confirmationRequiredMessage }}
      </p>

      <m-checkbox
        v-model="store.state.confirmationAcknowledgedAndProceed"
        label="Acknowledge and proceed"
      />
    </m-banner>

    <m-banner v-if="computedRepeatReauthoriseBannerMessage" type="info">
      {{ computedRepeatReauthoriseBannerMessage }}
    </m-banner>

    <m-banner v-if="store.state.canCancelOnEPS && store.state.epsCancellationError" type="warning">
      {{ store.state.epsCancellationError }}
    </m-banner>
    <m-banner v-else-if="store.state.canCancelOnEPS" type="warning">
      <p class="q-mb-md whitespace-pre-wrap">
        The patient still has medication that can be collected from the pharmacy on the old
        prescription. If you would like to request cancellation of this medication on the Spine then
        please confirm below and Medicus will attempt to cancel it.
      </p>

      <m-checkbox v-model="store.form.requestCancellation" label="Request cancellation" />
    </m-banner>
  </MCreateForm>
</template>
<style lang="scss">
.create-prescription-form {
  .q-field__bottom--animated {
    right: inherit !important;
    padding-left: 0;
  }

  .q-field__counter {
    padding-left: 0;
  }
}
</style>
